/**
 * IntegrationsPage - Admin page for managing system integrations
 * Features: List view, Detail view with tabs (Overview, Credentials, Health/Logs, Settings)
 */
import { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../components/ui/dialog';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { API_BASE } from '../lib/apiBase';
import {
    Plug,
    ArrowLeft,
    Plus,
    CheckCircle,
    XCircle,
    AlertTriangle,
    HelpCircle,
    RefreshCw,
    Settings,
    Key,
    Activity,
    Eye,
    EyeOff,
    Play,
    Webhook,
    Cloud,
    Database,
    ChevronRight,
    Clock,
    Loader2
} from 'lucide-react';

const API = API_BASE;

const TABS = [
    { id: 'overview', label: 'Overview', icon: Eye },
    { id: 'credentials', label: 'Credentials', icon: Key },
    { id: 'health', label: 'Health & Logs', icon: Activity },
    { id: 'settings', label: 'Settings', icon: Settings },
];

const TYPE_ICONS = {
    webhook: Webhook,
    api: Cloud,
    storage: Database,
};

const HEALTH_CONFIG = {
    healthy: { label: 'Healthy', color: 'text-emerald-600', bg: 'bg-emerald-50', icon: CheckCircle },
    degraded: { label: 'Degraded', color: 'text-amber-600', bg: 'bg-amber-50', icon: AlertTriangle },
    down: { label: 'Down', color: 'text-red-600', bg: 'bg-red-50', icon: XCircle },
    unknown: { label: 'Unknown', color: 'text-slate-500', bg: 'bg-slate-100', icon: HelpCircle },
};

const ENV_CONFIG = {
    production: { label: 'Production', color: 'text-emerald-600', bg: 'bg-emerald-50' },
    staging: { label: 'Staging', color: 'text-amber-600', bg: 'bg-amber-50' },
    development: { label: 'Development', color: 'text-blue-600', bg: 'bg-blue-50' },
};

// ==================== Integration List View ====================
function IntegrationListView() {
    const navigate = useNavigate();
    const [integrations, setIntegrations] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [newIntegration, setNewIntegration] = useState({
        name: '',
        type: 'api',
        provider: '',
        environment: 'production',
        description: ''
    });
    const [isCreating, setIsCreating] = useState(false);

    useEffect(() => {
        fetchIntegrations();
    }, []);

    const fetchIntegrations = async () => {
        setIsLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API}/api/integrations`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setIntegrations(response.data || []);
        } catch (error) {
            toast.error('Failed to load integrations');
        } finally {
            setIsLoading(false);
        }
    };

    const handleToggle = async (integration, e) => {
        e.stopPropagation();
        try {
            const token = localStorage.getItem('token');
            const response = await axios.patch(
                `${API}/api/integrations/${integration.id}/toggle`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setIntegrations(prev => prev.map(i => 
                i.id === integration.id ? { ...i, is_active: response.data.is_active } : i
            ));
            toast.success(`Integration ${response.data.is_active ? 'activated' : 'deactivated'}`);
        } catch (error) {
            toast.error('Failed to toggle integration');
        }
    };

    const handleCreate = async () => {
        if (!newIntegration.name.trim() || !newIntegration.provider.trim()) {
            toast.error('Name and provider are required');
            return;
        }
        setIsCreating(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.post(
                `${API}/api/integrations`,
                newIntegration,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Integration created');
            setShowCreateModal(false);
            setNewIntegration({ name: '', type: 'api', provider: '', environment: 'production', description: '' });
            navigate(`/admin/integrations/${response.data.id}`);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to create integration');
        } finally {
            setIsCreating(false);
        }
    };

    const getHealthBadge = (status) => {
        const config = HEALTH_CONFIG[status] || HEALTH_CONFIG.unknown;
        const Icon = config.icon;
        return (
            <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.color}`}>
                <Icon className="h-3 w-3" />
                {config.label}
            </span>
        );
    };

    const getEnvBadge = (env) => {
        const config = ENV_CONFIG[env] || ENV_CONFIG.development;
        return (
            <span className={`px-2 py-0.5 rounded text-xs font-medium ${config.bg} ${config.color}`}>
                {config.label}
            </span>
        );
    };

    const getTypeIcon = (type) => {
        const Icon = TYPE_ICONS[type] || Cloud;
        return <Icon className="h-4 w-4 text-slate-400" />;
    };

    const formatDate = (dateString) => {
        if (!dateString) return '—';
        return new Date(dateString).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <div className="p-6 max-w-7xl mx-auto">
            {/* Back Button */}
            <button
                onClick={() => navigate('/dashboard')}
                className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-4 transition-colors"
            >
                <ArrowLeft className="h-5 w-5" />
                <span className="text-sm font-medium">Back</span>
            </button>

            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                    <Plug className="h-6 w-6 text-slate-600" />
                    <div>
                        <h1 className="text-xl font-semibold text-slate-900">Integrations</h1>
                        <p className="text-sm text-slate-500">Manage external service connections</p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={fetchIntegrations}>
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Refresh
                    </Button>
                    <Button onClick={() => setShowCreateModal(true)}>
                        <Plus className="h-4 w-4 mr-2" />
                        Add Integration
                    </Button>
                </div>
            </div>

            {/* Integrations Table */}
            <div className="border rounded-lg overflow-hidden">
                <Table>
                    <TableHeader>
                        <TableRow className="bg-background">
                            <TableHead>Integration</TableHead>
                            <TableHead className="w-[100px]">Type</TableHead>
                            <TableHead className="w-[110px]">Environment</TableHead>
                            <TableHead className="w-[100px]">Status</TableHead>
                            <TableHead className="w-[100px]">Health</TableHead>
                            <TableHead className="w-[130px]">Last Success</TableHead>
                            <TableHead className="w-[130px]">Last Error</TableHead>
                            <TableHead className="w-[60px]"></TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {isLoading ? (
                            <TableRow>
                                <TableCell colSpan={8} className="text-center py-8 text-slate-400">
                                    Loading integrations...
                                </TableCell>
                            </TableRow>
                        ) : integrations.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={8} className="text-center py-8 text-slate-400">
                                    No integrations configured
                                </TableCell>
                            </TableRow>
                        ) : (
                            integrations.map((integration) => (
                                <TableRow 
                                    key={integration.id}
                                    className="hover:bg-background cursor-pointer"
                                    onClick={() => navigate(`/admin/integrations/${integration.id}`)}
                                >
                                    <TableCell>
                                        <div className="flex items-center gap-3">
                                            {getTypeIcon(integration.type)}
                                            <div>
                                                <p className="font-medium text-slate-900">{integration.name}</p>
                                                <p className="text-xs text-slate-400">{integration.provider}</p>
                                            </div>
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <span className="text-xs text-slate-500 capitalize">{integration.type}</span>
                                    </TableCell>
                                    <TableCell>{getEnvBadge(integration.environment)}</TableCell>
                                    <TableCell>
                                        <Switch
                                            checked={integration.is_active}
                                            onCheckedChange={(e) => handleToggle(integration, { stopPropagation: () => {} })}
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                    </TableCell>
                                    <TableCell>{getHealthBadge(integration.health_status)}</TableCell>
                                    <TableCell className="text-xs text-slate-500">
                                        {formatDate(integration.last_success_at)}
                                    </TableCell>
                                    <TableCell className="text-xs text-red-500">
                                        {formatDate(integration.last_error_at)}
                                    </TableCell>
                                    <TableCell>
                                        <ChevronRight className="h-4 w-4 text-slate-400" />
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>

            {/* Create Integration Modal */}
            <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>Add Integration</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <label className="text-sm font-medium">Name *</label>
                            <Input
                                value={newIntegration.name}
                                onChange={(e) => setNewIntegration({ ...newIntegration, name: e.target.value })}
                                placeholder="My Integration"
                            />
                        </div>
                        <div className="space-y-2">
                            <label className="text-sm font-medium">Provider *</label>
                            <Input
                                value={newIntegration.provider}
                                onChange={(e) => setNewIntegration({ ...newIntegration, provider: e.target.value })}
                                placeholder="e.g., stripe, sendgrid, aws_s3"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <label className="text-sm font-medium">Type</label>
                                <Select 
                                    value={newIntegration.type} 
                                    onValueChange={(v) => setNewIntegration({ ...newIntegration, type: v })}
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="api">API</SelectItem>
                                        <SelectItem value="webhook">Webhook</SelectItem>
                                        <SelectItem value="storage">Storage</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <label className="text-sm font-medium">Environment</label>
                                <Select 
                                    value={newIntegration.environment} 
                                    onValueChange={(v) => setNewIntegration({ ...newIntegration, environment: v })}
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="production">Production</SelectItem>
                                        <SelectItem value="staging">Staging</SelectItem>
                                        <SelectItem value="development">Development</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div className="space-y-2">
                            <label className="text-sm font-medium">Description</label>
                            <Textarea
                                value={newIntegration.description}
                                onChange={(e) => setNewIntegration({ ...newIntegration, description: e.target.value })}
                                placeholder="Optional description"
                                rows={2}
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowCreateModal(false)}>
                            Cancel
                        </Button>
                        <Button onClick={handleCreate} disabled={isCreating}>
                            {isCreating ? 'Creating...' : 'Create'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

// ==================== Integration Detail View ====================
function IntegrationDetailView({ integrationId }) {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const activeTab = searchParams.get('tab') || 'overview';
    
    const [integration, setIntegration] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        fetchIntegration();
    }, [integrationId]);

    const fetchIntegration = async () => {
        setIsLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API}/api/integrations/${integrationId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setIntegration(response.data);
        } catch (error) {
            toast.error('Failed to load integration');
            navigate('/admin/integrations');
        } finally {
            setIsLoading(false);
        }
    };

    const setActiveTab = (tab) => {
        setSearchParams({ tab });
    };

    if (isLoading) {
        return (
            <div className="p-6 max-w-7xl mx-auto">
                <div className="text-center py-12 text-slate-400">Loading integration...</div>
            </div>
        );
    }

    if (!integration) return null;

    const healthConfig = HEALTH_CONFIG[integration.health_status] || HEALTH_CONFIG.unknown;
    const HealthIcon = healthConfig.icon;

    return (
        <div className="p-6 max-w-7xl mx-auto">
            {/* Back Button */}
            <button
                onClick={() => navigate('/admin/integrations')}
                className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-4 transition-colors"
            >
                <ArrowLeft className="h-5 w-5" />
                <span className="text-sm font-medium">Back to Integrations</span>
            </button>

            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                    <Plug className="h-6 w-6 text-slate-600" />
                    <div>
                        <div className="flex items-center gap-2">
                            <h1 className="text-xl font-semibold text-slate-900">{integration.name}</h1>
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${healthConfig.bg} ${healthConfig.color}`}>
                                <HealthIcon className="h-3 w-3" />
                                {healthConfig.label}
                            </span>
                        </div>
                        <p className="text-sm text-slate-500">{integration.provider} • {integration.type}</p>
                    </div>
                </div>
                <Switch
                    checked={integration.is_active}
                    onCheckedChange={async () => {
                        try {
                            const token = localStorage.getItem('token');
                            const response = await axios.patch(
                                `${API}/api/integrations/${integrationId}/toggle`,
                                {},
                                { headers: { Authorization: `Bearer ${token}` } }
                            );
                            setIntegration({ ...integration, is_active: response.data.is_active });
                            toast.success(`Integration ${response.data.is_active ? 'activated' : 'deactivated'}`);
                        } catch (error) {
                            toast.error('Failed to toggle integration');
                        }
                    }}
                />
            </div>

            {/* Tabs */}
            <div className="border-b mb-6">
                <div className="flex gap-6">
                    {TABS.map((tab) => {
                        const Icon = tab.icon;
                        const isActive = activeTab === tab.id;
                        return (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`flex items-center gap-2 py-3 border-b-2 transition-colors ${
                                    isActive
                                        ? 'border-blue-600 text-blue-600'
                                        : 'border-transparent text-slate-500 hover:text-slate-700'
                                }`}
                            >
                                <Icon className="h-4 w-4" />
                                <span className="text-sm font-medium">{tab.label}</span>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Tab Content */}
            {activeTab === 'overview' && <OverviewTab integration={integration} onUpdate={fetchIntegration} />}
            {activeTab === 'credentials' && <CredentialsTab integration={integration} onUpdate={fetchIntegration} />}
            {activeTab === 'health' && <HealthTab integration={integration} />}
            {activeTab === 'settings' && <SettingsTab integration={integration} onUpdate={fetchIntegration} />}
        </div>
    );
}

// ==================== Overview Tab ====================
function OverviewTab({ integration, onUpdate }) {
    const [isTesting, setIsTesting] = useState(false);
    const [testResult, setTestResult] = useState(null);

    const handleTest = async () => {
        setIsTesting(true);
        setTestResult(null);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.post(
                `${API}/api/integrations/${integration.id}/test`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setTestResult(response.data);
            if (response.data.success) {
                toast.success('Connection test passed');
            } else {
                toast.error(response.data.message || 'Connection test failed');
            }
            onUpdate();
        } catch (error) {
            setTestResult({ success: false, message: error.response?.data?.detail || 'Test failed' });
            toast.error('Connection test failed');
        } finally {
            setIsTesting(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Never';
        return new Date(dateString).toLocaleString('en-GB');
    };

    return (
        <div className="space-y-6">
            {/* Quick Stats */}
            <div className="grid grid-cols-4 gap-4">
                <div className="border rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-slate-900">{integration.total_requests || 0}</p>
                    <p className="text-sm text-slate-500">Total Requests</p>
                </div>
                <div className="border rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-emerald-600">{integration.success_count || 0}</p>
                    <p className="text-sm text-slate-500">Successful</p>
                </div>
                <div className="border rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-red-600">{integration.error_count || 0}</p>
                    <p className="text-sm text-slate-500">Errors</p>
                </div>
                <div className="border rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-slate-900">
                        {integration.total_requests > 0 
                            ? Math.round((integration.success_count / integration.total_requests) * 100) 
                            : 0}%
                    </p>
                    <p className="text-sm text-slate-500">Success Rate</p>
                </div>
            </div>

            {/* Info Card */}
            <div className="border rounded-lg p-4">
                <h3 className="font-medium text-slate-900 mb-4">Integration Details</h3>
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <p className="text-xs text-slate-400">Provider</p>
                        <p className="text-sm font-medium">{integration.provider}</p>
                    </div>
                    <div>
                        <p className="text-xs text-slate-400">Type</p>
                        <p className="text-sm font-medium capitalize">{integration.type}</p>
                    </div>
                    <div>
                        <p className="text-xs text-slate-400">Environment</p>
                        <p className="text-sm font-medium capitalize">{integration.environment}</p>
                    </div>
                    <div>
                        <p className="text-xs text-slate-400">Status</p>
                        <p className="text-sm font-medium">{integration.is_active ? 'Active' : 'Inactive'}</p>
                    </div>
                    <div>
                        <p className="text-xs text-slate-400">Last Success</p>
                        <p className="text-sm font-medium">{formatDate(integration.last_success_at)}</p>
                    </div>
                    <div>
                        <p className="text-xs text-slate-400">Last Error</p>
                        <p className="text-sm font-medium text-red-600">{formatDate(integration.last_error_at)}</p>
                    </div>
                </div>
                {integration.description && (
                    <div className="mt-4 pt-4 border-t">
                        <p className="text-xs text-slate-400">Description</p>
                        <p className="text-sm text-slate-600">{integration.description}</p>
                    </div>
                )}
            </div>

            {/* Test Connection */}
            <div className="border rounded-lg p-4">
                <div className="flex items-center justify-between">
                    <div>
                        <h3 className="font-medium text-slate-900">Test Connection</h3>
                        <p className="text-sm text-slate-500">Verify the integration is working correctly</p>
                    </div>
                    <Button onClick={handleTest} disabled={isTesting}>
                        {isTesting ? (
                            <>
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                Testing...
                            </>
                        ) : (
                            <>
                                <Play className="h-4 w-4 mr-2" />
                                Test Connection
                            </>
                        )}
                    </Button>
                </div>
                {testResult && (
                    <div className={`mt-4 p-3 rounded-lg ${testResult.success ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
                        <div className="flex items-center gap-2">
                            {testResult.success ? <CheckCircle className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
                            <span className="font-medium">{testResult.success ? 'Success' : 'Failed'}</span>
                        </div>
                        <p className="text-sm mt-1">{testResult.message}</p>
                    </div>
                )}
            </div>
        </div>
    );
}

// ==================== Credentials Tab ====================
function CredentialsTab({ integration, onUpdate }) {
    const [credentials, setCredentials] = useState({});
    const [showSecrets, setShowSecrets] = useState({});
    const [isSaving, setIsSaving] = useState(false);

    const handleChange = (key, value) => {
        setCredentials({ ...credentials, [key]: value });
    };

    const toggleShowSecret = (key) => {
        setShowSecrets({ ...showSecrets, [key]: !showSecrets[key] });
    };

    const handleSave = async () => {
        // Only save credentials that have been changed
        const changedCreds = Object.entries(credentials)
            .filter(([key, value]) => value !== '')
            .map(([key, value]) => {
                const schema = integration.credentials?.find(c => c.key === key);
                return {
                    key,
                    value,
                    is_secret: schema?.is_secret ?? true,
                    description: schema?.description
                };
            });

        if (changedCreds.length === 0) {
            toast.info('No changes to save');
            return;
        }

        setIsSaving(true);
        try {
            const token = localStorage.getItem('token');
            await axios.put(
                `${API}/api/integrations/${integration.id}/credentials`,
                { credentials: changedCreds },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Credentials updated');
            setCredentials({});
            onUpdate();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to update credentials');
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="space-y-6">
            <div className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h3 className="font-medium text-slate-900">API Credentials</h3>
                        <p className="text-sm text-slate-500">
                            Secrets are masked after saving. Enter a new value to update.
                        </p>
                    </div>
                </div>

                <div className="space-y-4">
                    {integration.credentials?.map((cred) => (
                        <div key={cred.key} className="space-y-1">
                            <label className="text-sm font-medium flex items-center gap-2">
                                {cred.key}
                                {cred.is_secret && <Key className="h-3 w-3 text-slate-400" />}
                                {cred.has_value && (
                                    <span className="text-xs text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded">
                                        Configured
                                    </span>
                                )}
                            </label>
                            {cred.description && (
                                <p className="text-xs text-slate-400">{cred.description}</p>
                            )}
                            <div className="flex gap-2">
                                <div className="relative flex-1">
                                    <Input
                                        type={cred.is_secret && !showSecrets[cred.key] ? 'password' : 'text'}
                                        value={credentials[cred.key] ?? (cred.is_secret ? '' : cred.value || '')}
                                        onChange={(e) => handleChange(cred.key, e.target.value)}
                                        placeholder={cred.has_value ? (cred.is_secret ? '••••••••' : cred.value) : 'Enter value'}
                                        className="pr-10"
                                        readOnly={!cred.is_secret && cred.has_value && !credentials[cred.key]}
                                    />
                                    {cred.is_secret && (
                                        <button
                                            type="button"
                                            onClick={() => toggleShowSecret(cred.key)}
                                            className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                                        >
                                            {showSecrets[cred.key] ? (
                                                <EyeOff className="h-4 w-4" />
                                            ) : (
                                                <Eye className="h-4 w-4" />
                                            )}
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="mt-6 pt-4 border-t flex justify-end">
                    <Button onClick={handleSave} disabled={isSaving}>
                        {isSaving ? 'Saving...' : 'Save Credentials'}
                    </Button>
                </div>
            </div>

            <div className="border rounded-lg p-4 bg-amber-50 border-amber-200">
                <div className="flex items-start gap-3">
                    <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                        <h4 className="font-medium text-amber-800">Security Notice</h4>
                        <p className="text-sm text-amber-700 mt-1">
                            Credentials are stored securely and never displayed in plaintext after saving.
                            Only users with admin access can view or modify credentials.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}

// ==================== Health & Logs Tab ====================
function HealthTab({ integration }) {
    const [logs, setLogs] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        fetchLogs();
    }, [integration.id]);

    const fetchLogs = async () => {
        setIsLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(
                `${API}/api/integrations/${integration.id}/logs?limit=50`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setLogs(response.data.logs || []);
        } catch (error) {
            toast.error('Failed to load logs');
        } finally {
            setIsLoading(false);
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleString('en-GB');
    };

    const getEventBadge = (eventType) => {
        const config = {
            request: { bg: 'bg-blue-50', color: 'text-blue-600' },
            response: { bg: 'bg-emerald-50', color: 'text-emerald-600' },
            error: { bg: 'bg-red-50', color: 'text-red-600' },
            test: { bg: 'bg-purple-50', color: 'text-purple-600' },
            credentials_updated: { bg: 'bg-amber-50', color: 'text-amber-600' },
            status_changed: { bg: 'bg-slate-100', color: 'text-slate-600' },
        };
        const c = config[eventType] || config.request;
        return (
            <span className={`px-2 py-0.5 rounded text-xs font-medium ${c.bg} ${c.color}`}>
                {eventType}
            </span>
        );
    };

    return (
        <div className="space-y-6">
            {/* Health Summary */}
            <div className="border rounded-lg p-4">
                <h3 className="font-medium text-slate-900 mb-4">Health Summary</h3>
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <p className="text-xs text-slate-400">Current Status</p>
                        <div className="flex items-center gap-2 mt-1">
                            {(() => {
                                const config = HEALTH_CONFIG[integration.health_status] || HEALTH_CONFIG.unknown;
                                const Icon = config.icon;
                                return (
                                    <>
                                        <Icon className={`h-5 w-5 ${config.color}`} />
                                        <span className={`font-medium ${config.color}`}>{config.label}</span>
                                    </>
                                );
                            })()}
                        </div>
                    </div>
                    <div>
                        <p className="text-xs text-slate-400">Last Error</p>
                        <p className="text-sm text-red-600 mt-1">
                            {integration.last_error_message || 'No recent errors'}
                        </p>
                    </div>
                </div>
            </div>

            {/* Recent Logs */}
            <div className="border rounded-lg overflow-hidden">
                <div className="p-4 bg-background border-b flex items-center justify-between">
                    <h3 className="font-medium text-slate-900">Recent Activity</h3>
                    <Button variant="outline" size="sm" onClick={fetchLogs}>
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Refresh
                    </Button>
                </div>
                {isLoading ? (
                    <div className="p-8 text-center text-slate-400">Loading logs...</div>
                ) : logs.length === 0 ? (
                    <div className="p-8 text-center text-slate-400">No activity logged yet</div>
                ) : (
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead className="w-[160px]">Time</TableHead>
                                <TableHead className="w-[120px]">Event</TableHead>
                                <TableHead className="w-[80px]">Status</TableHead>
                                <TableHead>Message</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {logs.map((log) => (
                                <TableRow key={log.id}>
                                    <TableCell className="text-xs text-slate-500">
                                        {formatDate(log.created_at)}
                                    </TableCell>
                                    <TableCell>{getEventBadge(log.event_type)}</TableCell>
                                    <TableCell>
                                        {log.status === 'success' ? (
                                            <CheckCircle className="h-4 w-4 text-emerald-600" />
                                        ) : (
                                            <XCircle className="h-4 w-4 text-red-600" />
                                        )}
                                    </TableCell>
                                    <TableCell className="text-sm text-slate-600">
                                        {log.message || '—'}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </div>
        </div>
    );
}

// ==================== Settings Tab ====================
function SettingsTab({ integration, onUpdate }) {
    const navigate = useNavigate();
    const [settings, setSettings] = useState({
        name: integration.name,
        description: integration.description || '',
        environment: integration.environment,
    });
    const [isSaving, setIsSaving] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    const handleSave = async () => {
        setIsSaving(true);
        try {
            const token = localStorage.getItem('token');
            await axios.put(
                `${API}/api/integrations/${integration.id}`,
                settings,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Settings updated');
            onUpdate();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to update settings');
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async () => {
        try {
            const token = localStorage.getItem('token');
            await axios.delete(
                `${API}/api/integrations/${integration.id}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Integration deleted');
            navigate('/admin/integrations');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to delete integration');
        }
    };

    return (
        <div className="space-y-6">
            <div className="border rounded-lg p-4">
                <h3 className="font-medium text-slate-900 mb-4">General Settings</h3>
                <div className="space-y-4">
                    <div className="space-y-2">
                        <label className="text-sm font-medium">Name</label>
                        <Input
                            value={settings.name}
                            onChange={(e) => setSettings({ ...settings, name: e.target.value })}
                        />
                    </div>
                    <div className="space-y-2">
                        <label className="text-sm font-medium">Description</label>
                        <Textarea
                            value={settings.description}
                            onChange={(e) => setSettings({ ...settings, description: e.target.value })}
                            rows={3}
                        />
                    </div>
                    <div className="space-y-2">
                        <label className="text-sm font-medium">Environment</label>
                        <Select 
                            value={settings.environment} 
                            onValueChange={(v) => setSettings({ ...settings, environment: v })}
                        >
                            <SelectTrigger className="w-[200px]">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="production">Production</SelectItem>
                                <SelectItem value="staging">Staging</SelectItem>
                                <SelectItem value="development">Development</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>
                <div className="mt-6 pt-4 border-t flex justify-end">
                    <Button onClick={handleSave} disabled={isSaving}>
                        {isSaving ? 'Saving...' : 'Save Settings'}
                    </Button>
                </div>
            </div>

            {/* Danger Zone */}
            {!integration.is_builtin && (
                <div className="border border-red-200 rounded-lg p-4 bg-red-50">
                    <h3 className="font-medium text-red-800 mb-2">Danger Zone</h3>
                    <p className="text-sm text-red-600 mb-4">
                        Deleting this integration will remove all associated credentials and logs.
                        This action cannot be undone.
                    </p>
                    <Button variant="destructive" onClick={() => setShowDeleteConfirm(true)}>
                        Delete Integration
                    </Button>
                </div>
            )}

            {integration.is_builtin && (
                <div className="border rounded-lg p-4 bg-background">
                    <p className="text-sm text-slate-500">
                        This is a built-in integration and cannot be deleted.
                    </p>
                </div>
            )}

            {/* Delete Confirmation Dialog */}
            <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Delete Integration?</DialogTitle>
                    </DialogHeader>
                    <p className="text-sm text-slate-600">
                        Are you sure you want to delete <strong>{integration.name}</strong>?
                        This will remove all credentials and logs associated with this integration.
                    </p>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>
                            Cancel
                        </Button>
                        <Button variant="destructive" onClick={handleDelete}>
                            Delete
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

// ==================== Main Component ====================
export default function IntegrationsPage() {
    const { integrationId } = useParams();

    if (integrationId) {
        return <IntegrationDetailView integrationId={integrationId} />;
    }

    return <IntegrationListView />;
}
